/**
 * MapaApoyoCotejo.tsx — Mapa de apoyo de P3 (S.UI.A3a-S1 · capas WMS S2).
 *
 * Mapa de apoyo en la ranura `panelIzq` del `WorkspaceShell` (sustituyó al
 * placeholder inicial, podado en T2PR 03-jul-2026):
 * mapa Leaflet real con base PNOA/OSM, contorno municipal (GeoJSON 4326, sin
 * relleno) y marcadores de los puntos «ubicados» del cotejo, coloreados por
 * `kind`. Vista de overview (todos los ubicados); table-primary: el mapa es apoyo.
 *
 * Incluye (A3a-S2) una capa WMS de Catastro INSPIRE OFF por defecto y un panel de
 * capas flotante (`LayersPanel`) que la alterna, con control de opacidad y aviso
 * de zoom (Catastro solo pinta a zoom ≥ 14).
 *
 * VECMUN-b: `VecinosCapa` pinta los términos vecinos (línea blanca fina) y los nombres
 * del propio y de los vecinos, en panes propios por debajo del contorno amarillo.
 * VECMUN-c: el dibujo del contorno propio (`ContornoPropio`, con `grosorContorno`) y la lectura
 * del zoom (`useZoomActual`) salen a `components/map`, para que los comparta el mapa de P2.
 *
 * Reusos: `TileProvider` (PNOA/OSM), `CatastroWMSLayer` (WMS INSPIRE · CC BY 4.0),
 * `LayersPanel` (primitiva S.UI.P3), `projections.fromUtm` (25830→4326), paleta
 * `kind` de `@/design-system/v2`. Sin glow ni box-shadow (regla dura).
 *
 * @version 1.3.0
 */

import { useEffect, useMemo, useState, type CSSProperties, type FC } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import type { LatLngTuple } from 'leaflet';
import type { Polygon, MultiPolygon } from 'geojson';
import { colors, typography, spacing, borderRadius } from '@/design-system/v2';
import { IGN_PNOA_CONFIG } from '@/services/tiles/TileProvider';
import { CatastroWMSLayer } from '@/components/map/CatastroWMSLayer';
import { ContornoPropio, grosorContorno } from '@/components/map/TerminoMunicipal';
import { useZoomActual } from '@/components/map/useZoomActual';
import { LayersPanel, type CapaLayer } from '../workspace';
import { fromUtm } from '@/utils/projections';
// VALMUNI-b2b2 (Luis, «Como P2 y P4»): las cuatro funciones salen del servicio, no del índice.
import {
  getBoundaryByINE,
  loadBoundaryByINE,
  getBoundaryLoadStatus,
  textoSinValidarContorno,
  type BoundaryLoadStatus,
} from '@/services/boundaries/MunicipalBoundaryService';
import type { MunicipalBoundary } from '@/lib/spatialValidation';
import type { CotejoItem, KindCotejo } from './cotejoItem';
import { VecinosCapa } from './VecinosCapa';

// Centro inicial (Andalucía) antes de que `fitBounds` ajuste a contorno∪puntos.
const CENTRO_ANDALUCIA: LatLngTuple = [37.5, -4.7];
const ZOOM_INICIAL = 9;
// Catastro INSPIRE solo sirve teselas a zoom ≥ 14 (cf. CatastroWMSLayer.minZoom).
const ZOOM_CATASTRO_MIN = 14;
const CATASTRO_OPACIDAD_DEF = 0.6;
// CONTP3 · estilo del contorno propio según el zoom: ahora en `components/map/TerminoMunicipal`
// (VECMUN-c), compartido con el mapa de P2.

const KIND_COLOR: Record<KindCotejo, string> = {
  ausente:  colors.error,
  incluido: colors.success,
  sobrante: colors.info,
};

const KIND_LABEL: Record<KindCotejo, string> = {
  ausente:  'Ausentes',
  incluido: 'Incluidos',
  sobrante: 'Sobrantes',
};

interface UbicadaPunto {
  id: string;
  nombre: string;
  kind: KindCotejo;
  pos: LatLngTuple;
}

export interface MapaApoyoCotejoProps {
  /** Código INE 5 dígitos del municipio (para el contorno). */
  ine5: string;
  /** Ítems del cotejo; los «ubicados» (con x/y 25830) se pintan como marcadores. */
  items: CotejoItem[];
  /** Nombre del municipio (opcional, para accesibilidad). */
  municipio?: string;
  /**
   * T3 · §6 — id del ítem a resaltar (selección compartida del buscador global
   * o de las cards). El marcador resaltado crece y refuerza el trazo — sin
   * glow ni box-shadow (regla dura DS v2). Def: null.
   */
  idResaltado?: string | null;
}

// ─── Componentes internos (operan sobre el mapa vía useMap) ─────────────────────

/** Ajusta la vista al contorno ∪ puntos cada vez que cambian. */
const FitBounds: FC<{ geometry: Polygon | MultiPolygon | null; points: LatLngTuple[] }> = ({
  geometry, points,
}) => {
  const map = useMap();
  useEffect(() => {
    const latlngs: LatLngTuple[] = [...points];
    if (geometry) {
      try {
        const gb = L.geoJSON(geometry).getBounds();
        if (gb.isValid()) {
          latlngs.push([gb.getSouth(), gb.getWest()], [gb.getNorth(), gb.getEast()]);
        }
      } catch { /* contorno inválido → se ignora para el encuadre */ }
    }
    if (latlngs.length === 0) return;
    const bounds = L.latLngBounds(latlngs);
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [16, 16], maxZoom: 16 });
  }, [map, geometry, points]);
  return null;
};

/** Revalida el tamaño del mapa cuando el panel cambia de ancho (panelIzq sin alto fijo). */
const ResizeAware: FC = () => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
};

/**
 * Propaga el nivel de zoom al consumidor (aviso de zoom de Catastro, grosor del contorno y nombres
 * de los términos vecinos). La lectura —`zoomend` y, al montarse, `getZoom()` (VECMUN-b: el estado
 * seguía en 9 con el mapa en 11 y los nombres no salían hasta tocar el zoom)— es ahora el hook
 * compartido `useZoomActual` (VECMUN-c); el estado sigue aquí porque el aviso de Catastro vive
 * fuera del `MapContainer`.
 */
const ZoomWatcher: FC<{ onZoom: (z: number) => void }> = ({ onZoom }) => {
  const zoomActual = useZoomActual();
  useEffect(() => {
    onZoom(zoomActual);
  }, [zoomActual, onZoom]);
  return null;
};

// ─── Estilos (DS v2; sin glow ni box-shadow) ────────────────────────────────────

const S: Record<string, CSSProperties> = {
  cont: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: colors.bg.secondary,
  },
  mapWrap: {
    flex: 1,
    minHeight: 200,
    position: 'relative',
  },
  pie: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[1],
    padding: `${spacing[2]} ${spacing[3]}`,
    borderTop: `1px solid ${colors.border.DEFAULT}`,
    background: colors.bg.secondary,
  },
  leyenda: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[2],
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.xs,
    color: colors.fg.muted,
  },
  // VALMUNI-b2b2: nota «sin validar contra el término municipal», en el pie (no tapa el mapa).
  avisoContorno: {
    margin: 0,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.xs,
    lineHeight: 1.4,
    color: colors.warning,
  },
  legItem: { display: 'inline-flex', alignItems: 'center', gap: spacing[1] },
  swatch: { width: 10, height: 10, borderRadius: borderRadius.sm, display: 'inline-block' },
  resumen: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.subtle,
  },
  sep: { color: colors.border.DEFAULT, userSelect: 'none' },
  // P2VOC-c2: sistema de coordenadas de las tarjetas (mismo acento que la
  // etiqueta CRS de la `StatusBar`, que P3 ya no monta).
  crs: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.primary,
  },
  // Panel de capas flotante (DOP-S2-1a): esquina superior derecha, colapsable.
  panelFlotante: {
    position: 'absolute',
    top: spacing[2],
    right: spacing[2],
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing[1],
  },
  colapsar: {
    appearance: 'none',
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    background: colors.bg.secondary,
    color: colors.fg.primary,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.xs,
    padding: `${spacing[1]} ${spacing[2]}`,
    cursor: 'pointer',
  },
  panelCuerpo: {
    width: 232,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    background: colors.bg.secondary,
  },
  zoomHint: {
    margin: 0,
    padding: `${spacing[1]} ${spacing[2]}`,
    borderTop: `1px solid ${colors.border.DEFAULT}`,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.subtle,
  },
};

// ─── Componente ─────────────────────────────────────────────────────────────────

export const MapaApoyoCotejo: FC<MapaApoyoCotejoProps> = ({ ine5, items, municipio, idResaltado = null }) => {
  const [boundary, setBoundary] = useState<MunicipalBoundary | null>(() => getBoundaryByINE(ine5));
  // VALMUNI-b2b2: estado del contorno con la misma caché; si falla, el pie lo dice.
  const [estadoContorno, setEstadoContorno] = useState<BoundaryLoadStatus>(() => getBoundaryLoadStatus(ine5));

  // Estado local de la capa Catastro WMS (DOP-S2-5a: se reinicia al desmontar P3).
  const [catastroVisible, setCatastroVisible] = useState(false);
  const [catastroOpacity, setCatastroOpacity] = useState(CATASTRO_OPACIDAD_DEF);
  const [zoom, setZoom] = useState(ZOOM_INICIAL);
  const [panelAbierto, setPanelAbierto] = useState(true);

  // Carga del contorno: cache caliente desde P2 o lazy por-INE.
  // Guard `active` contra setState tras desmontaje (HX-UNHANDLED-REJECTION-ENMASCARA-VERDE).
  useEffect(() => {
    let active = true;
    const warm = getBoundaryByINE(ine5);
    if (warm) {
      setBoundary(warm);
      setEstadoContorno('cargado');
      return;
    }
    setBoundary(null);
    setEstadoContorno(getBoundaryLoadStatus(ine5));
    void loadBoundaryByINE(ine5).then((b) => {
      if (!active) return;
      setBoundary(b);
      setEstadoContorno(getBoundaryLoadStatus(ine5));
    });
    return () => { active = false; };
  }, [ine5]);

  // VALMUNI-b2b2 (DOP-3 a′): sin INE de 5 cifras útil, sin nota. Texto sellado, sin reescribir.
  const ineUtil = /^\d{5}$/.test(ine5) && ine5 !== '00000';
  const avisoContorno = ineUtil ? textoSinValidarContorno(municipio || ine5, estadoContorno) : null;

  // Puntos «ubicados» (con x/y 25830) reproyectados a 4326 para Leaflet.
  const ubicadas = useMemo<UbicadaPunto[]>(() => {
    const out: UbicadaPunto[] = [];
    for (const it of items) {
      if (it.x == null || it.y == null) continue;
      try {
        const { lat, lng } = fromUtm(it.x, it.y, 'EPSG:25830');
        out.push({ id: it.id, nombre: it.nombre, kind: it.kind, pos: [lat, lng] });
      } catch { /* coordenada inválida → se omite del mapa */ }
    }
    return out;
  }, [items]);

  // P2VOC-c: P3 cuenta tarjetas del cotejo (plan + fuentes), no filas del plan:
  // dice lo que cuenta («con coordenada» / «sin coordenada») y no usa el
  // vocabulario de estado de P2 («ubicadas», «por revisar»).
  const nConCoordenada = ubicadas.length;
  const nSinCoordenada = items.length - nConCoordenada;
  const puntos = useMemo<LatLngTuple[]>(() => ubicadas.map((u) => u.pos), [ubicadas]);

  // Modelo de capas del panel (DOP-S2-2a: solo «Catastro», 1 overlay OFF por defecto).
  const capas = useMemo<CapaLayer[]>(() => [{
    id: 'catastro',
    nombre: 'Catastro (parcelas y edificios)',
    visible: catastroVisible,
    tipo: 'overlay',
    opacidad: catastroOpacity,
  }], [catastroVisible, catastroOpacity]);

  const onToggleCapa = (id: string) => {
    if (id === 'catastro') setCatastroVisible((v) => !v);
  };
  const onOpacidadCapa = (id: string, valor: number) => {
    if (id === 'catastro') setCatastroOpacity(valor);
  };

  // Aviso de zoom (DOP-S2-4b): Catastro activo pero por debajo del umbral de pintado.
  const avisoZoom = catastroVisible && zoom < ZOOM_CATASTRO_MIN;

  return (
    <div
      data-testid="coteja-mapa-apoyo"
      style={S.cont}
      aria-label={`Mapa de apoyo${municipio ? ` de ${municipio}` : ''}`}
    >
      <div style={S.mapWrap}>
        <MapContainer center={CENTRO_ANDALUCIA} zoom={ZOOM_INICIAL} style={{ height: '100%', width: '100%' }} zoomControl>
          {/* Z-order (DOP-S2-6): base PNOA → Catastro WMS → contorno → marcadores.
              VECMUN-b: vecinos (pane 390) y nombres (395) quedan por debajo del contorno y los puntos. */}
          <TileLayer
            url={IGN_PNOA_CONFIG.url}
            attribution={IGN_PNOA_CONFIG.attribution}
            maxZoom={IGN_PNOA_CONFIG.maxZoom}
          />
          <CatastroWMSLayer
            visible={catastroVisible}
            opacity={catastroOpacity}
            onError={() => { /* WMS Catastro: fallo silencioso; la capa simplemente no pinta */ }}
          />
          {/* CONTP3: el gris de 2 px no se leía sobre la ortofoto. Halo oscuro discontinuo
              debajo y línea amarilla discontinua encima, con el mismo patrón de trazos. */}
          {boundary && <ContornoPropio boundary={boundary} zoom={zoom} />}
          {/* VECMUN-b: después del contorno propio; el apilado lo fijan los panes (390/395 < 400),
              el orden del JSX solo mantiene el de `getAllByTestId('geojson')` en las pruebas. */}
          <VecinosCapa ine5={ine5} zoom={zoom} grosor={grosorContorno(zoom)} />
          {ubicadas.map((u) => {
            const resaltado = u.id === idResaltado;
            return (
              <CircleMarker
                key={u.id}
                center={u.pos}
                radius={resaltado ? 9 : 6}
                pathOptions={{
                  color: resaltado ? colors.fg.primary : KIND_COLOR[u.kind],
                  weight: resaltado ? 3 : 2,
                  fillColor: KIND_COLOR[u.kind],
                  fillOpacity: resaltado ? 1 : 0.85,
                }}
              >
                <Tooltip>{u.nombre}</Tooltip>
              </CircleMarker>
            );
          })}
          <FitBounds geometry={boundary?.geometry ?? null} points={puntos} />
          <ResizeAware />
          <ZoomWatcher onZoom={setZoom} />
        </MapContainer>

        <div style={S.panelFlotante}>
          <button
            type="button"
            data-testid="coteja-capas-colapsar"
            aria-expanded={panelAbierto}
            aria-label={panelAbierto ? 'Contraer panel de capas' : 'Mostrar panel de capas'}
            style={S.colapsar}
            onClick={() => setPanelAbierto((o) => !o)}
          >
            {panelAbierto ? '▾' : 'Capas ▸'}
          </button>
          {panelAbierto && (
            <div style={S.panelCuerpo}>
              <LayersPanel
                capas={capas}
                onToggleVisible={onToggleCapa}
                onOpacidad={onOpacidadCapa}
              />
              {avisoZoom && (
                <p data-testid="coteja-catastro-zoom-hint" style={S.zoomHint}>
                  Catastro visible al acercar (zoom ≥ 14)
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={S.pie}>
        {avisoContorno && (
          <p data-testid="coteja-apoyo-sin-validar-contorno" role="note" style={S.avisoContorno}>
            {avisoContorno}
          </p>
        )}
        <div style={S.leyenda}>
          {(Object.keys(KIND_LABEL) as KindCotejo[]).map((k) => (
            <span key={k} style={S.legItem}>
              <span style={{ ...S.swatch, background: KIND_COLOR[k] }} aria-hidden="true" />
              {KIND_LABEL[k]}
            </span>
          ))}
        </div>
        <div style={S.resumen}>
          <span data-testid="coteja-apoyo-con-coordenada">{nConCoordenada} con coordenada</span>
          <span style={S.sep}>·</span>
          <span data-testid="coteja-apoyo-sin-coordenada">{nSinCoordenada} sin coordenada</span>
        </div>
        <div data-testid="coteja-apoyo-epsg" style={S.crs}>EPSG:25830</div>
      </div>
    </div>
  );
};

export default MapaApoyoCotejo;
